import { combineEpics, ofType } from 'redux-observable'
import { EMPTY, interval, of } from 'rxjs'
import { catchError, filter, startWith, switchMap, takeUntil, takeWhile } from 'rxjs/operators'

import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import {
  selectActualFeatureFlags,
  selectActualLicenses,
  selectHasSelectedCompanyFilterAccess,
} from 'containers/App/selectors'
import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'

import { selectHasIncidentWidgetAccess, selectIncidentsWidgetsVisibility } from '../selectors'

import { selectEndUserFilter, SET_END_USER_FILTER, urgentUnclassifiedIncidentsRedux } from './redux'

const TEN_MINUTES = 10 * 60 * 1000

// Session expiry: DRF session auth answers 403 (not 401) once the session dies, and the Sentry
// trail showed a long-lived tab ticking 403s endlessly. So one 401/403 ends the polling loop.
// Restart triggers (outer switchMap): urgent INITIATE, company data load (home URL only), end-user filter toggle.
const isAuthFailure = (error) => error?.status === 401 || error?.status === 403

const pollingEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      urgentUnclassifiedIncidentsRedux.INITIATE,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      SET_END_USER_FILTER
    ),
    filter((action) =>
      action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
        ? matchURL(URL_CONST.dashboard.home)
        : true
    ),
    switchMap(() => {
      const state = state$.value
      // INITIATE and company-data success turn the loader on. Cancel so a child
      // the user cannot swap into does not leave the widget spinning.
      if (!selectHasSelectedCompanyFilterAccess(state)) {
        return of(urgentUnclassifiedIncidentsRedux.cancel())
      }
      const hasUserPermissions = selectIncidentsWidgetsVisibility(state)
      const hasCompanyPermissions = selectHasIncidentWidgetAccess(state)
      const licenses = selectActualLicenses(state)
      const ff = selectActualFeatureFlags(state)
      const featureFlagsEnabled = ff.ENABLE_AUTO_THEMIS || licenses.incident_management_service
      const shouldInitPolling = hasUserPermissions && hasCompanyPermissions && !featureFlagsEnabled
      if (!shouldInitPolling) {
        return EMPTY
      }
      return interval(TEN_MINUTES).pipe(
        startWith(0),
        switchMap(() => {
          // Read at tick time: the tab outlives company switches and permission reloads.
          const tickState = state$.value
          const companyId = selectCompanyFilter(tickState)?.value
          const endUser = selectEndUserFilter(tickState)

          return api.dashboard.getUnclassifiedIncidents(companyId, endUser).pipe(
            switchMap((response) =>
              of(urgentUnclassifiedIncidentsRedux.onSuccess(response?.response))
            ),
            catchError((error) =>
              of(
                epicsErrorHandler(
                  urgentUnclassifiedIncidentsRedux.onError,
                  error,
                  isAuthFailure(error) ? [error.status] : []
                )
              )
            )
          )
        }),
        takeWhile(
          (action) =>
            action.type !== urgentUnclassifiedIncidentsRedux.ERROR || !isAuthFailure(action.error),
          true
        ),
        takeUntil(action$.pipe(ofType(urgentUnclassifiedIncidentsRedux.CANCEL)))
      )
    })
  )

export default combineEpics(pollingEpic)
